import _ from "lodash";
import React, { ReactElement, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { DataTable, Loading } from "../ui";
import DataTableEditableCell from "../ui/DataTableEditableCell";
import { Tag } from "./Tag";
import { TagCreateForm } from "./TagCreateForm";
import { TagService } from "./TagService";

const DeleteCell = (props: any) => {
	const [confirming, setConfirming] = useState(false);

	if (confirming) {
		return (
			<span>
				<Button
					size="sm"
					variant="danger"
					onClick={() => props.cell.column.onDelete(props.row.index)}
					style={{ marginRight: 6 }}
				>
					Confirm
				</Button>
				<Button size="sm" variant="outline-secondary" onClick={() => setConfirming(false)}>
					Cancel
				</Button>
			</span>
		);
	}

	return (
		<Button size="sm" variant="outline-danger" onClick={() => setConfirming(true)}>
			<i className="fa fa-trash" />
		</Button>
	);
};

export const TagAdminPage = (): ReactElement => {
	const [tags, setTags] = useState<Tag[]>();
	const [showCreate, setShowCreate] = useState(false);
	const [flash, setFlash] = useState<string | null>(null);

	const tagService = new TagService();

	useMemo(() => {
		const fetchTags = async () => {
			const tagData = await tagService.findAll();
			setTags(tagData);
		};

		fetchTags();
	}, []);

	if (!tags) {
		return <Loading />;
	}

	const showFlash = (msg: string) => {
		setFlash(msg);
		setTimeout(() => setFlash(null), 3000);
	};

	const onUpdateName = (index: number, value: string) => {
		const newTags = [...tags];
		_.set(newTags, `[${index}].name`, value);
		tagService.save(newTags[index]);
		setTags(newTags);
		showFlash("Tag updated");
	};

	const onDelete = async (index: number) => {
		const tag = tags[index];
		await tagService.delete(tag);
		const newTags = tags.filter((_, i) => i !== index);
		setTags(newTags);
		showFlash(`"${tag.name}" deleted`);
	};

	const columns = [
		{
			Header: "ID",
			accessor: "id",
			width: "10%",
			disableFilters: true,
		},
		{
			Header: "Name",
			accessor: "name",
			width: "75%",
			Cell: DataTableEditableCell,
			onUpdate: (index: number, value: string) => {
				onUpdateName(index, value);
			},
		},
		{
			Header: "",
			id: "actions",
			accessor: (row: Tag) => row.id,
			width: "15%",
			disableFilters: true,
			Cell: DeleteCell,
			onDelete: (index: number) => {
				onDelete(index);
			},
		},
	];

	return (
		<div className="container mb-3" id="tags-admin">
			{/* Page header */}
			<div className="forms-page-header">
				<div className="forms-page-header-left">
					<div className="forms-page-icon">
						<i className="fa fa-tag" />
					</div>
					<div>
						<h1>Tags</h1>
						<span className="forms-page-count">
							{tags.length} tag{tags.length !== 1 ? "s" : ""}
						</span>
					</div>
				</div>
				<Button
					variant={showCreate ? "outline-secondary" : "primary"}
					onClick={() => setShowCreate(!showCreate)}
					className="forms-create-toggle"
				>
					<i className={`fa ${showCreate ? "fa-times" : "fa-plus"}`} />{" "}
					{showCreate ? "Cancel" : "New Tag"}
				</Button>
			</div>

			{/* Flash message */}
			{flash && (
				<div className="forms-flash">
					<i className="fa fa-check-circle" /> {flash}
				</div>
			)}

			{/* Create form (collapsible) */}
			{showCreate && (
				<Card className="mb-4 forms-create-card">
					<Card.Body>
						<TagCreateForm
							onCreate={tag => {
								setTags([tag, ...tags]);
								setShowCreate(false);
								showFlash(`"${tag.name}" created`);
							}}
						/>
					</Card.Body>
				</Card>
			)}

			{/* Data table */}
			<div className="editable-data-table forms-table-wrapper">
				<DataTable columns={columns} data={tags} />
			</div>
		</div>
	);
};
